import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FaIconComponent, NotificationComponent } from '@akostm/orbit-ui';
import {
  faBell,
  faBoxArchive,
  faBoxesPacking,
  faChevronDown,
  faChevronRight,
  faEllipsis,
  faFileLines,
  faImage,
  faLink,
  faMicrophone,
  faPhone,
  faUserGroup,
  faVideo,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { faFlag as faEmptyFlag, faGripDotsVertical } from '@fortawesome/pro-regular-svg-icons';
import { faFlag as faFilledFlag } from '@fortawesome/duotone-regular-svg-icons';
import { SidebarButtonType } from '../../../interfaces';
import { CdkDragHandle } from '@angular/cdk/drag-drop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ActiveConversationStore, ConversationStore, TenantStore } from '../../../stores';
import { ConversationHelperService, NotificationTrackingService } from '../../../services';
import { Conversation, Participant } from '@clientwire/api';
import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { ConfirmDialogComponent } from '../dialogs';
import { ToastrService } from 'ngx-toastr';
import { Clipboard, ClipboardModule } from '@angular/cdk/clipboard';
import { ConnectedPosition, Overlay } from '@angular/cdk/overlay';
import { FlagListComponent } from '../flag-list/flag-list.component';
import { ComponentPortal } from '@angular/cdk/portal';
import { logger } from '../../../utils';
import { faLock, faUnlock } from '@fortawesome/pro-solid-svg-icons';
import {
  LockConversationComponent,
  LockConversationData,
} from '../dialogs/lock-conversation-dialog/lock-conversation.component';

@Component({
  selector: 'shared-conversation-item-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    FaIconComponent,
    NotificationComponent,
    CdkDragHandle,
    TranslatePipe,
    ClipboardModule,
  ],
  templateUrl: './conversation-item-sidebar.component.html',
  styleUrl: './conversation-item-sidebar.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.collapsed]': 'isCollapsed()',
    '[class.active]': 'isActive()',
  },
})
export class ConversationItemSidebarComponent {
  //#region Inputs
  readonly conversationId = input.required<string>();
  readonly userId = input.required<string | undefined>();
  readonly isCollapsed = input<boolean>(false);
  readonly isActive = input<boolean | undefined>(false);
  readonly activeIcon = input<SidebarButtonType | null>(null);
  readonly label = input<string>();
  readonly notifications = input<number>(0);
  readonly dot = input<boolean>(false);
  readonly isDraggable = input<boolean>(false);
  readonly isCloseable = input<boolean>(false);
  readonly conversationData = input<Partial<Conversation> | undefined>(undefined);
  readonly isConversationArchived = input(false);
  //#endregion

  //#region Outputs
  readonly toggleParticipantList = output<void>();
  //#endregion

  //#region Computed
  readonly selectedFlagColor = computed(() => this.#getFlagColorFromConversation());
  readonly conversation = computed(
    () => this.#activeConversationStore.entityMap()[this.conversationId()]
  );
  readonly isConversationLocked = computed(() => {
    const conversation = this.conversationData();
    if (conversation?.lockExpiresAt) {
      return new Date() < conversation.lockExpiresAt;
    }
    return false;
  });
  //#endregion

  readonly SidebarButtonTypeEnum = SidebarButtonType;

  //#region Services
  readonly #dialog = inject(Dialog);
  readonly #translateService = inject(TranslateService);
  readonly #activeConversationStore = inject(ActiveConversationStore);
  readonly #conversationStore = inject(ConversationStore);
  readonly #tenantStore = inject(TenantStore);
  readonly #conversationHelperService = inject(ConversationHelperService);
  readonly #toastr = inject(ToastrService);
  readonly #clipboard = inject(Clipboard);
  readonly #overlay = inject(Overlay);
  readonly #notificationTrackingService = inject(NotificationTrackingService);
  //#endregion

  //#region Icons
  readonly xMarkIcon = faXmark;
  readonly gripVertical = faGripDotsVertical;
  readonly bell = faBell;
  readonly userGroupIcon = faUserGroup;
  readonly downIcon = faChevronDown;
  readonly rightIcon = faChevronRight;
  readonly ellipsisIcon = faEllipsis;
  readonly fileIcon = faFileLines;
  readonly imageIcon = faImage;
  readonly videoIcon = faVideo;
  readonly microphoneIcon = faMicrophone;
  readonly phoneIcon = faPhone;
  readonly archiveIcon = faBoxArchive;
  readonly unarchiveIcon = faBoxesPacking;
  readonly linkIcon = faLink;
  readonly filledFlagIcon = faFilledFlag;
  readonly emptyFlag = faEmptyFlag;
  readonly lockIcon = faLock;
  readonly unlock = faUnlock;
  //#endregion

  getCollapseIcon() {
    if (!this.isCollapsed()) {
      return this.downIcon;
    }
    return this.rightIcon;
  }

  sidebarClicked(value: SidebarButtonType, event: Event) {
    const conversation = this.conversation();
    switch (value) {
      case SidebarButtonType.Participants: {
        this.toggleParticipantList.emit();
        break;
      }
      case 'CLOSE': {
        this.#closeConversation(this.conversationId());
        break;
      }
      case SidebarButtonType.Archive: {
        if (!conversation) throw Error('Conversation is not defined');
        this.#confirmArchiveCommand(conversation);
        break;
      }
      case SidebarButtonType.Unarchive: {
        if (!conversation) throw Error('Conversation is not defined');
        this.#confirmArchiveCommand(conversation, true);
        break;
      }
      case 'NOTIFY': {
        if (!conversation) throw Error('Conversation is not defined');
        this.#confirmNotify(conversation);
        break;
      }
      case 'SHARE': {
        let frontendUrl = this.#tenantStore.frontendUrl();
        if (!frontendUrl) frontendUrl = window.location.origin;
        this.#clipboard.copy(`${frontendUrl}/?conversationId=${this.conversationId()}`);
        this.#toastr.success(
          this.#translateService.instant('SHARED.PAGES.DASHBOARD.SHARE_SUCCESS'),
          undefined,
          {
            toastClass: 'ngx-toastr toastr-success-clipboard-copy',
          }
        );
        break;
      }
      case 'FLAG': {
        this.#openFlagMenu(event.target as HTMLElement);
        break;
      }
      case 'LOCK': {
        if (!conversation) throw Error('Conversation is not defined');
        this.#lockConversation(conversation);
        break;
      }
      case 'UNLOCK': {
        if (!conversation) throw Error('Conversation is not defined');
        this.#confirmUnlock(conversation);
        break;
      }
      default: {
        logger.debug(this.conversationId(), event);
        break;
      }
    }
  }

  #closeConversation(id: string) {
    const isActive = this.#activeConversationStore.entityMap()[id];
    if (isActive) {
      this.#activeConversationStore.removeConversation(id);
      this.#conversationHelperService.unsubscribeFromConversation(id);
    }
  }

  #confirmArchiveCommand(conversation: Conversation, unarchive = false) {
    const title = unarchive
      ? this.#translateService.instant('SHARED.PAGES.DASHBOARD.CONFIRM_UNARCHIVE.TITLE')
      : this.#translateService.instant('SHARED.PAGES.DASHBOARD.CONFIRM_ARCHIVE.TITLE');
    const message = unarchive
      ? this.#translateService.instant('SHARED.PAGES.DASHBOARD.CONFIRM_UNARCHIVE.MESSAGE')
      : this.#translateService.instant('SHARED.PAGES.DASHBOARD.CONFIRM_ARCHIVE.MESSAGE');
    const confirmLabel = unarchive
      ? this.#translateService.instant('SHARED.PAGES.DASHBOARD.CONFIRM_UNARCHIVE.LABEL')
      : this.#translateService.instant('SHARED.PAGES.DASHBOARD.CONFIRM_ARCHIVE.LABEL');
    const cancelLabel = unarchive
      ? this.#translateService.instant('SHARED.PAGES.DASHBOARD.CONFIRM_UNARCHIVE.CANCEL')
      : this.#translateService.instant('SHARED.PAGES.DASHBOARD.CONFIRM_ARCHIVE.CANCEL');

    const id = conversation.id;
    const dialogRef = this.#dialog.open(ConfirmDialogComponent, {
      data: {
        title,
        message,
        confirmLabel,
        cancelLabel,
      },
    });

    dialogRef.closed.subscribe((result) => {
      if (result) {
        if (unarchive) {
          this.#unarchiveConversation(id);
        } else {
          this.#archiveConversation(id);
        }
      }
    });
  }

  #confirmNotify(conversation: Conversation) {
    const participantsContactInformation = conversation.participants
      ?.filter((participant) => participant.kind === 'CLIENT')
      ?.map((participant: Participant) => {
        const { displayName, email, phoneNumber } = participant;
        const contactInfo = [email, phoneNumber].filter(Boolean).join(', ');
        return contactInfo ? `${displayName} (${contactInfo})` : displayName;
      })
      .join(', ');
    const dialogRef = this.#dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.#translateService.instant('SHARED.PAGES.DASHBOARD.CONFIRM_NOTIFY.TITLE'),
        message: this.#translateService.instant('SHARED.PAGES.DASHBOARD.CONFIRM_NOTIFY.MESSAGE', {
          info: participantsContactInformation,
        }),
        confirmLabel: this.#translateService.instant('SHARED.PAGES.DASHBOARD.CONFIRM_NOTIFY.LABEL'),
        confirmIcon: 'paper-plane',
        cancelLabel: this.#translateService.instant('SHARED.PAGES.DASHBOARD.CONFIRM_NOTIFY.CANCEL'),
      },
    });

    dialogRef.closed.subscribe((result) => {
      if (result) {
        // Generate correlation UUID for this notification request
        const correlationUuid = crypto.randomUUID();
        
        // Track the correlation UUID BEFORE making the API call
        // This prevents duplicate toasts when websocket messages arrive before the API response
        this.#notificationTrackingService.trackNotificationRequest(correlationUuid);

        this.#activeConversationStore.notifyParticipants(conversation.id, correlationUuid).then((response) => {
          
          // Ensure we have a valid response
          if (!response) {
            console.error('No response received from notifyParticipants');
            return;
          }

          // Show appropriate toast based on the response
          // Compute success state from actual numbers (don't rely on computed properties from backend)
          const totalNotifications = response.totalNotifications ?? 0;
          const successfulNotifications = response.successfulNotifications ?? 0;
          const failedNotifications = response.failedNotifications ?? 0;
          
          const allSuccessful = totalNotifications > 0 && failedNotifications === 0;
          const hasPartialSuccess = successfulNotifications > 0 && failedNotifications > 0;
          const allFailed = totalNotifications > 0 && successfulNotifications === 0;

          if (allSuccessful) {
            // All notifications sent successfully
            const title = this.#translateService.instant(
              'SHARED.PAGES.DASHBOARD.CONFIRM_NOTIFY.NOTIFIED_SUCCESS.TITLE'
            );
            const message = this.#translateService.instant(
              'SHARED.PAGES.DASHBOARD.CONFIRM_NOTIFY.NOTIFIED_SUCCESS.MESSAGE',
              { info: participantsContactInformation }
            );
            this.#toastr.success(message, title, {
              toastClass: 'ngx-toastr toastr-success-notification-initiated',
            });
          } else if (hasPartialSuccess) {
            // Partial success
            const title = this.#translateService.instant(
              'SHARED.PAGES.DASHBOARD.CONFIRM_NOTIFY.NOTIFIED_PARTIAL.TITLE'
            );
            const message = this.#translateService.instant(
              'SHARED.PAGES.DASHBOARD.CONFIRM_NOTIFY.NOTIFIED_PARTIAL.MESSAGE',
              {
                successful: successfulNotifications,
                total: totalNotifications
              }
            );
            this.#toastr.warning(message, title, {
              toastClass: 'ngx-toastr toastr-warning-notification-partial',
            });
          } else if (allFailed) {
            // All notifications failed
            const title = this.#translateService.instant(
              'SHARED.PAGES.DASHBOARD.CONFIRM_NOTIFY.NOTIFIED_FAILED.TITLE'
            );
            const message = this.#translateService.instant(
              'SHARED.PAGES.DASHBOARD.CONFIRM_NOTIFY.NOTIFIED_FAILED.MESSAGE'
            );
            this.#toastr.error(message, title, {
              toastClass: 'ngx-toastr toastr-error-notification-failed',
            });
          } else {
            // No notifications attempted or edge case
            console.warn('Unexpected notification state:', {
              totalNotifications,
              successfulNotifications,
              failedNotifications
            });
          }
        }).catch((error) => {
          // Handle API error
          console.error('Error notifying participants:', error);
          
          const title = this.#translateService.instant(
            'SHARED.PAGES.DASHBOARD.CONFIRM_NOTIFY.NOTIFIED_ERROR.TITLE'
          );
          const message = this.#translateService.instant(
            'SHARED.PAGES.DASHBOARD.CONFIRM_NOTIFY.NOTIFIED_ERROR.MESSAGE'
          );
          this.#toastr.error(message, title);
        });
      }
    });
  }

  #openFlagMenu(event: Element) {
    if (event) {
      const positionStrategy = this.#overlay
        .position()
        .flexibleConnectedTo(event)
        .withPositions([
          {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'top',
          } as ConnectedPosition,
        ])
        .withPush(false);
      const scrollStrategy = this.#overlay.scrollStrategies.close();
      const overlayRef = this.#overlay.create({
        positionStrategy: positionStrategy,
        scrollStrategy: scrollStrategy,
      });
      const more = new ComponentPortal(FlagListComponent);
      const ref = overlayRef.attach(more);
      ref.instance.conversationData.set(this.conversation());
      ref.instance.closeDropdown.subscribe(() => {
        overlayRef.detach();
      });
      overlayRef._outsidePointerEvents.subscribe(() => {
        overlayRef.detach();
      });
    }
  }

  #getFlagColorFromConversation() {
    const conversation = this.conversation();
    if (conversation) {
      const labels = conversation.labels as string[];
      return labels?.find((it) => it.includes('flag:'))?.split(':')[1];
    }
    return '';
  }

  #lockConversation(conversation: Conversation) {
    const id = conversation.id;
    const dialogRef: DialogRef<LockConversationData, LockConversationComponent> = this.#dialog.open(
      LockConversationComponent,
      {
        data: {
          title: this.#translateService.instant('SHARED.PAGES.DASHBOARD.CONFIRM_ARCHIVE.TITLE'),
          message: this.#translateService.instant('SHARED.PAGES.DASHBOARD.CONFIRM_ARCHIVE.MESSAGE'),
          confirmLabel: this.#translateService.instant(
            'SHARED.PAGES.DASHBOARD.CONFIRM_ARCHIVE.LABEL'
          ),
          cancelLabel: this.#translateService.instant(
            'SHARED.PAGES.DASHBOARD.CONFIRM_ARCHIVE.CANCEL'
          ),
        },
      }
    );

    dialogRef.closed.subscribe((result: LockConversationData | undefined) => {
      if (result) {
        this.#conversationHelperService.lockConversation(id, {
          lockNote: result.lockNote,
          lockExpiresAt: result.lockExpiresAt,
          lockedBy: this.userId(),
        });
      }
    });
  }

  #confirmUnlock(conversation: Conversation) {
    const id = conversation.id;
    const dialogRef = this.#dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.#translateService.instant('SHARED.PAGES.DASHBOARD.CONFIRM_UNLOCK.TITLE'),
        message: this.#translateService.instant('SHARED.PAGES.DASHBOARD.CONFIRM_UNLOCK.MESSAGE'),
        confirmLabel: this.#translateService.instant('SHARED.PAGES.DASHBOARD.CONFIRM_UNLOCK.LABEL'),
        cancelLabel: this.#translateService.instant('SHARED.PAGES.DASHBOARD.CONFIRM_UNLOCK.CANCEL'),
      },
    });

    dialogRef.closed.subscribe((result) => {
      if (result) {
        this.#conversationHelperService.unlockConversation(id);
      }
    });
  }

  #archiveConversation(conversationId: string) {
    if (conversationId) {
      this.#conversationHelperService.archiveAndCloseConversation(conversationId);
    }
  }
  #unarchiveConversation(conversationId: string) {
    if (conversationId) {
      this.#conversationStore.unarchiveConversation(conversationId);
    }
  }

  onSidebarKeyDown(event: KeyboardEvent, buttonType: SidebarButtonType): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.sidebarClicked(buttonType, event);
    }
  }
}
